import React from 'react';
import { Controller, useFormContext } from 'react-hook-form';

import {
  feetToFtIn,
  ftInToDecimalFeet,
  MAX_LIMITATION_LENGTH,
  MIN_QUESTIONNAIRE_AGE,
  parseNumberInput,
  PLAN_REVIEW_COPY,
} from '@personal-trainer-app/shared/src/utils/questionnaire/questionnaire-form';
import {
  FITNESS_GOAL_OPTIONS,
  FITNESS_CHALLENGE_OPTIONS,
  GOAL_TIMELINE_OPTIONS,
  MOTIVATION_OPTIONS,
  QUESTIONNAIRE_GENDER_OPTIONS,
  WORKOUT_TYPE_OPTIONS,
} from '@personal-trainer-app/shared/src/utils/select-options';

import { ChipMultiSelect } from '../ChipMultiSelect';
import { FormSlider } from '../FormSlider';
import { FITNESS_LEVEL_CARD_OPTIONS } from '../levelOptions';
import { PlanReviewNotice } from '../PlanReviewNotice';
import { SetupStep } from '../SetupStep';

import type { HeightFieldProps, SectionProps } from './RefinePlanForm.types';
import type { FitnessQuestionnaireInput } from '@personal-trainer-app/shared';

import {
  FormField,
  FormInput,
  FormSelectize,
  FormTextarea,
} from '@/components/forms';
import { ChoiceCardSelect } from '@/components/ui/ChoiceCardSelect';
import { RadioGroup } from '@/components/ui/Radio';
import { Toggle } from '@/components/ui/Toggle';
import { useUserPreferences } from '@/hooks/settings';

const MIN_AGE = MIN_QUESTIONNAIRE_AGE;

/** A titled group of related fields. */
function Section({
  title,
  description,
  children,
}: SectionProps): React.JSX.Element {
  return (
    <section className="space-y-5 border-t border-stroke-subtle pt-6 first:border-t-0 first:pt-0">
      <div>
        <h3 className="text-label-md font-semibold uppercase tracking-wide text-neutral-400">
          {title}
        </h3>
        {description && (
          <p className="mt-1 text-body-sm text-neutral-500">{description}</p>
        )}
      </div>
      {children}
    </section>
  );
}

/**
 * Height input — unit-aware, no conversion. Metric is a single `cm` field;
 * imperial is a feet + inches pair composed into the decimal-feet value the
 * backend round-trips (matching the Measurements convention).
 */
function HeightField({
  value,
  onChange,
  system,
  error,
}: HeightFieldProps): React.JSX.Element {
  if (system === 'imperial') {
    const { ft, inches } =
      value != null ? feetToFtIn(value) : { ft: 0, inches: 0 };
    const hasValue = value != null;
    const update = (nextFt: number, nextIn: number): void => {
      if (nextFt === 0 && nextIn === 0) onChange(undefined);
      else onChange(ftInToDecimalFeet(nextFt, nextIn));
    };
    return (
      <FormField label="Height" error={error}>
        <div className="flex items-end gap-3">
          <FormInput
            type="number"
            inputMode="numeric"
            min={0}
            aria-label="Height (feet)"
            placeholder="ft"
            value={hasValue ? String(ft) : ''}
            onChange={(e) =>
              update(parseNumberInput(e.target.value) ?? 0, inches)
            }
          />
          <FormInput
            type="number"
            inputMode="numeric"
            min={0}
            max={11}
            aria-label="Height (inches)"
            placeholder="in"
            value={hasValue ? String(inches) : ''}
            onChange={(e) => update(ft, parseNumberInput(e.target.value) ?? 0)}
          />
        </div>
      </FormField>
    );
  }

  return (
    <FormInput
      type="number"
      inputMode="decimal"
      min={0}
      label="Height"
      placeholder="cm"
      value={value != null ? String(value) : ''}
      onChange={(e) => onChange(parseNumberInput(e.target.value))}
      error={error}
    />
  );
}

/**
 * RefinePlanForm — the full editable criteria for the refine modal, rendered
 * inside a shared `FormProvider`. Groups the required spine plus the optional
 * "make it sharper" fields. Presentational: the modal owns fetch/submit.
 */
export function RefinePlanForm(): React.JSX.Element {
  const {
    control,
    watch,
    setValue,
    formState: { errors },
  } = useFormContext<FitnessQuestionnaireInput>();
  const { preferences } = useUserPreferences();
  const system = preferences.measurement_system;

  const hasLimitations = watch('has_limitations');
  const limitationText = watch('limitation_description') ?? '';
  const motivation = watch('motivation');
  const biggestChallenge = watch('biggest_challenge');
  const weightUnit = system === 'metric' ? 'kg' : 'lbs';

  return (
    <div className="space-y-8">
      {/* ---- Goals -------------------------------------------------------- */}
      <Section title="Goals">
        <Controller
          name="primary_fitness_goals"
          control={control}
          render={({ field }) => (
            <FormField
              label="What are you working toward?"
              helpText="Choose all that apply."
              error={errors.primary_fitness_goals?.message}
            >
              <ChipMultiSelect
                options={FITNESS_GOAL_OPTIONS}
                value={field.value ?? []}
                onChange={field.onChange}
                allowOther
                ariaLabel="Fitness goals"
              />
            </FormField>
          )}
        />
        <Controller
          name="goal_timeline"
          control={control}
          render={({ field }) => (
            <RadioGroup
              name="goal_timeline"
              label="By when?"
              variant="pill"
              options={[...GOAL_TIMELINE_OPTIONS]}
              value={field.value ?? ''}
              onChange={field.onChange}
              error={errors.goal_timeline?.message}
            />
          )}
        />
        <Controller
          name="goal_priority"
          control={control}
          render={({ field }) => (
            <FormSlider
              label="How much does this matter right now?"
              helpText="Optional — 1 (nice to have) to 10 (top priority)."
              min={1}
              max={10}
              value={field.value}
              onChange={field.onChange}
              error={errors.goal_priority?.message}
            />
          )}
        />
      </Section>

      {/* ---- Starting point ---------------------------------------------- */}
      <Section title="Starting point">
        <Controller
          name="current_fitness_level"
          control={control}
          render={({ field }) => (
            <FormField error={errors.current_fitness_level?.message}>
              <ChoiceCardSelect
                options={FITNESS_LEVEL_CARD_OPTIONS}
                value={field.value}
                onChange={field.onChange}
                ariaLabel="Current fitness level"
              />
            </FormField>
          )}
        />
      </Section>

      {/* ---- Watch-outs -------------------------------------------------- */}
      <Section title="Watch-outs">
        <Controller
          name="has_limitations"
          control={control}
          render={({ field }) => (
            <Toggle
              checked={field.value ?? false}
              onChange={(checked) => {
                field.onChange(checked);
                if (!checked) setValue('limitation_description', undefined);
              }}
              label="Any injuries or limitations?"
              description="We'll tailor your plan to train around them."
            />
          )}
        />
        {hasLimitations && <PlanReviewNotice />}
        {hasLimitations && (
          <Controller
            name="limitation_description"
            control={control}
            render={({ field }) => (
              <FormTextarea
                label={PLAN_REVIEW_COPY.descriptionLabel}
                placeholder="e.g. Left knee — avoid deep squats."
                rows={3}
                maxLength={MAX_LIMITATION_LENGTH}
                value={field.value ?? ''}
                onChange={(e) => field.onChange(e.target.value)}
                error={errors.limitation_description?.message}
                helpText={`${PLAN_REVIEW_COPY.descriptionHelp} ${limitationText.length}/${MAX_LIMITATION_LENGTH}`}
              />
            )}
          />
        )}
      </Section>

      {/* ---- About you --------------------------------------------------- */}
      <Section
        title="About you"
        description="Helps us pitch volume and intensity. All optional."
      >
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
          <Controller
            name="age"
            control={control}
            render={({ field }) => (
              <FormInput
                type="number"
                inputMode="numeric"
                min={MIN_AGE}
                label="Age"
                placeholder="years"
                value={field.value != null ? String(field.value) : ''}
                onChange={(e) =>
                  field.onChange(parseNumberInput(e.target.value))
                }
                error={errors.age?.message}
              />
            )}
          />
          <Controller
            name="gender"
            control={control}
            render={({ field }) => {
              const options = [...QUESTIONNAIRE_GENDER_OPTIONS];
              const selected =
                options.find((o) => o.value === field.value) ?? null;
              return (
                <FormSelectize
                  label="Gender"
                  placeholder="Prefer not to say"
                  options={options}
                  value={selected}
                  onChange={(option) => {
                    const next = Array.isArray(option) ? option[0] : option;
                    field.onChange(next?.value ?? undefined);
                  }}
                  error={errors.gender?.message}
                />
              );
            }}
          />
          <Controller
            name="height"
            control={control}
            render={({ field }) => (
              <HeightField
                value={field.value}
                onChange={field.onChange}
                system={system}
                error={errors.height?.message}
              />
            )}
          />
          <Controller
            name="weight"
            control={control}
            render={({ field }) => (
              <FormInput
                type="number"
                inputMode="decimal"
                min={0}
                label={`Weight (${weightUnit})`}
                placeholder={weightUnit}
                value={field.value != null ? String(field.value) : ''}
                onChange={(e) =>
                  field.onChange(parseNumberInput(e.target.value))
                }
                error={errors.weight?.message}
              />
            )}
          />
        </div>
      </Section>

      {/* ---- Your routine — the sign-up Setup step's fields, plus favourites */}
      <Section title="Your routine">
        <SetupStep context="refine" />
        <Controller
          name="favourite_workouts"
          control={control}
          render={({ field }) => (
            <FormField
              label="What do you enjoy?"
              helpText="Choose all that apply."
            >
              <ChipMultiSelect
                options={WORKOUT_TYPE_OPTIONS}
                value={field.value ?? []}
                onChange={field.onChange}
                ariaLabel="Favourite workouts"
              />
            </FormField>
          )}
        />
      </Section>

      {/* ---- What keeps you going ---------------------------------------- */}
      <Section title="What keeps you going">
        <Controller
          name="motivation"
          control={control}
          render={({ field }) => {
            const options = [...MOTIVATION_OPTIONS];
            const selected =
              options.find((o) => o.value === field.value) ?? null;
            return (
              <FormSelectize
                label="What's driving you most?"
                placeholder="Choose one"
                options={options}
                value={selected}
                onChange={(option) => {
                  const next = Array.isArray(option) ? option[0] : option;
                  field.onChange(next?.value ?? undefined);
                }}
                error={errors.motivation?.message}
              />
            );
          }}
        />
        {motivation === 'other' && (
          <Controller
            name="motivation_other"
            control={control}
            render={({ field }) => (
              <FormInput
                label="Tell us more"
                placeholder="What's motivating you?"
                value={field.value ?? ''}
                onChange={(e) => field.onChange(e.target.value)}
                error={errors.motivation_other?.message}
              />
            )}
          />
        )}
        <Controller
          name="biggest_challenge"
          control={control}
          render={({ field }) => {
            const options = [...FITNESS_CHALLENGE_OPTIONS];
            const selected =
              options.find((o) => o.value === field.value) ?? null;
            return (
              <FormSelectize
                label="What gets in the way?"
                placeholder="Choose one"
                options={options}
                value={selected}
                onChange={(option) => {
                  const next = Array.isArray(option) ? option[0] : option;
                  field.onChange(next?.value ?? undefined);
                }}
                error={errors.biggest_challenge?.message}
              />
            );
          }}
        />
        {biggestChallenge === 'other' && (
          <Controller
            name="biggest_challenge_other"
            control={control}
            render={({ field }) => (
              <FormInput
                label="Tell us more"
                placeholder="What's the main blocker?"
                value={field.value ?? ''}
                onChange={(e) => field.onChange(e.target.value)}
                error={errors.biggest_challenge_other?.message}
              />
            )}
          />
        )}
      </Section>
    </div>
  );
}

export default RefinePlanForm;
